/**
 * What the Audio Comparer shows besides the score: loudness (EBU R128 / ITU-R BS.1770 integrated LUFS), peak,
 * the side-by-side properties table, the aligned difference signal, and the maths behind the A/B listening
 * toggle (loudness match and start alignment). Everything here is pure and runs in the browser.
 */
import { encodeWav } from "./silence";

export interface LoudnessInfo {
  /** Integrated loudness in LUFS, or null when the file is shorter than one 400 ms block or silent. */
  lufs: number | null;
  /** Highest absolute sample, dBFS (-Infinity for digital silence). Sample peak, not inter-sample true peak. */
  peakDb: number;
}

type Biquad = { b: [number, number, number]; a: [number, number] };

/** BS.1770 K-weighting: a high-shelf then a high-pass, derived for any sample rate. */
function kWeighting(fs: number): [Biquad, Biquad] {
  const shelf = (() => {
    const f0 = 1681.974450955533, G = 3.999843853973347, Q = 0.7071752369554196;
    const K = Math.tan((Math.PI * f0) / fs);
    const Vh = Math.pow(10, G / 20), Vb = Math.pow(Vh, 0.4996667741545416);
    const a0 = 1 + K / Q + K * K;
    return { b: [(Vh + (Vb * K) / Q + K * K) / a0, (2 * (K * K - Vh)) / a0, (Vh - (Vb * K) / Q + K * K) / a0], a: [(2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0] } as Biquad;
  })();
  const hp = (() => {
    const f0 = 38.13547087602444, Q = 0.5003270373238773;
    const K = Math.tan((Math.PI * f0) / fs);
    const a0 = 1 + K / Q + K * K;
    return { b: [1, -2, 1], a: [(2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0] } as Biquad;
  })();
  return [shelf, hp];
}

function filt(x: Float32Array | Float64Array, f: Biquad): Float64Array {
  const y = new Float64Array(x.length);
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const v = f.b[0] * x[i] + f.b[1] * x1 + f.b[2] * x2 - f.a[0] * y1 - f.a[1] * y2;
    x2 = x1; x1 = x[i]; y2 = y1; y1 = v; y[i] = v;
  }
  return y;
}

export function measureLoudness(channels: Float32Array[], sampleRate: number): LoudnessInfo {
  const n = channels[0]?.length ?? 0;
  let peak = 0;
  for (const ch of channels) for (let i = 0; i < n; i++) { const a = Math.abs(ch[i]); if (a > peak) peak = a; }
  const peakDb = peak > 0 ? 20 * Math.log10(peak) : -Infinity;
  const block = Math.round(0.4 * sampleRate), step = Math.round(0.1 * sampleRate);
  if (n < block || peak === 0) return { lufs: null, peakDb };
  const [s1, s2] = kWeighting(sampleRate);
  const weighted = channels.map((ch) => filt(filt(ch, s1), s2));
  // Mean square of each 400 ms block (75% overlap), summed over channels (all weight 1: mono, stereo).
  const z: number[] = [];
  for (let start = 0; start + block <= n; start += step) {
    let sum = 0;
    for (const w of weighted) { let s = 0; for (let i = start; i < start + block; i++) s += w[i] * w[i]; sum += s / block; }
    z.push(sum);
  }
  const lk = (m: number) => -0.691 + 10 * Math.log10(m);
  const above = z.filter((m) => m > 0 && lk(m) > -70);
  if (!above.length) return { lufs: null, peakDb };
  const relGate = lk(above.reduce((s, m) => s + m, 0) / above.length) - 10;
  const kept = above.filter((m) => lk(m) > relGate);
  if (!kept.length) return { lufs: null, peakDb };
  return { lufs: lk(kept.reduce((s, m) => s + m, 0) / kept.length), peakDb };
}

/** Gains for the A/B toggle: the louder file is turned down to the quieter one's level (never boosted, so no clipping). */
export function matchGains(lufsA: number | null, lufsB: number | null, on: boolean): { a: number; b: number } {
  if (!on || lufsA === null || lufsB === null) return { a: 1, b: 1 };
  const d = Math.max(-40, Math.min(40, lufsA - lufsB));
  return d > 0 ? { a: Math.pow(10, -d / 20), b: 1 } : { a: 1, b: Math.pow(10, d / 20) };
}

/**
 * Starting a file at position `pos` seconds of file 1's timeline. File 2's sound starts `offset` seconds later (+)
 * or earlier (-) than file 1's, so file 2's own time is pos + offset. When that is before its start, wait `delay`
 * seconds and start from 0, so the two sound the same at the same moment when you flip between them.
 */
export function alignedStart(pos: number, offset: number, side: "A" | "B"): { delay: number; from: number } {
  const t = side === "A" ? pos : pos + offset;
  return t >= 0 ? { delay: 0, from: t } : { delay: -t, from: 0 };
}

/** File 2 on file 1's timeline: sample i of the result is file 2's sample at i + shift (zero outside file 2). */
export function alignToFirst(a: Float32Array, b: Float32Array, offsetSeconds: number, rate: number): { a: Float32Array; b: Float32Array } {
  const shift = Math.round(offsetSeconds * rate);
  const out = new Float32Array(a.length);
  for (let i = 0; i < a.length; i++) { const j = i + shift; if (j >= 0 && j < b.length) out[i] = b[j]; }
  return { a, b: out };
}

export interface DiffSignal {
  samples: Float32Array;
  /** True when every sample is exactly equal after alignment: the audio data is bit-identical (at the decoded rate). */
  bitIdentical: boolean;
  /** Gain applied to file 2 to bring it to file 1's loudness before subtracting (dB); 0 when loudness is unknown. */
  matchDb: number;
  peak: number;
}

/** file 1 minus file 2 (aligned and loudness-matched): silence where they agree, sound where they differ. */
export function differenceSignal(a: Float32Array, b: Float32Array, offsetSeconds: number, rate: number, lufsA: number | null, lufsB: number | null): DiffSignal {
  const al = alignToFirst(a, b, offsetSeconds, rate);
  const raw = (() => { let eq = true; for (let i = 0; i < a.length; i++) if (a[i] !== al.b[i]) { eq = false; break; } return eq; })();
  const matchDb = raw || lufsA === null || lufsB === null ? 0 : Math.max(-40, Math.min(40, lufsA - lufsB));
  const g = Math.pow(10, matchDb / 20);
  const d = new Float32Array(a.length);
  let peak = 0;
  for (let i = 0; i < a.length; i++) { d[i] = a[i] - g * al.b[i]; const v = Math.abs(d[i]); if (v > peak) peak = v; }
  return { samples: d, bitIdentical: raw, matchDb, peak };
}

/** WAV bytes of the difference, boosted (when very quiet) so it can be heard; `boostDb` says by how much. */
export function differenceWav(diff: DiffSignal, rate: number): { bytes: Uint8Array; boostDb: number } {
  let boostDb = 0;
  let samples = diff.samples;
  if (diff.peak > 0 && diff.peak < 0.5) {
    boostDb = Math.min(60, -20 * Math.log10(diff.peak / 0.89));
    const g = Math.pow(10, boostDb / 20);
    samples = Float32Array.from(diff.samples, (v) => v * g);
  }
  return { bytes: encodeWav([samples], rate), boostDb: Math.round(boostDb * 10) / 10 };
}

/** Per-column peak (0..1) for drawing. */
export function binPeaks(x: Float32Array, bins: number): Float32Array {
  const out = new Float32Array(bins);
  if (!x.length || bins < 1) return out;
  const size = x.length / bins;
  for (let b = 0; b < bins; b++) {
    const lo = Math.floor(b * size), hi = Math.min(x.length, Math.max(lo + 1, Math.floor((b + 1) * size)));
    let m = 0;
    for (let i = lo; i < hi; i++) { const v = Math.abs(x[i]); if (v > m) m = v; }
    out[b] = m;
  }
  return out;
}

export interface FileFacts {
  name: string;
  seconds: number;
  /** Rate read from the file's own header, when known. */
  fileRate: number | null;
  /** Rate the browser decoded to (browsers resample to the audio device's rate, so this can differ from the file). */
  decodedRate: number;
  channels: number;
  bytes: number;
  lufs: number | null;
  peakDb: number;
}

export interface PropertyRow { label: string; a: string; b: string; same: boolean | null; note?: string }

const fmtDbVal = (v: number, unit: string) => (Number.isFinite(v) ? `${v.toFixed(1)} ${unit}` : "silent");
const fmtSize = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(2)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmtDur = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(2).padStart(5, "0")}`;
const chName = (c: number) => (c === 1 ? "mono" : c === 2 ? "stereo" : `${c} channels`);

/** The Same / Different table. The sample-rate row says where the number came from, because browsers resample. */
export function propertyRows(a: FileFacts, b: FileFacts): PropertyRow[] {
  const rate = (f: FileFacts) => (f.fileRate ? `${f.fileRate} Hz` : `${f.decodedRate} Hz (as decoded)`);
  const rateKnown = a.fileRate !== null && b.fileRate !== null;
  return [
    { label: "Duration", a: fmtDur(a.seconds), b: fmtDur(b.seconds), same: Math.abs(a.seconds - b.seconds) < 0.05 },
    { label: "Sample rate", a: rate(a), b: rate(b), same: rateKnown ? a.fileRate === b.fileRate : null, note: rateKnown ? undefined : "At least one rate could not be read from the file; browsers resample, so the decoded rate is not reliable." },
    { label: "Channels", a: chName(a.channels), b: chName(b.channels), same: a.channels === b.channels },
    { label: "File size", a: fmtSize(a.bytes), b: fmtSize(b.bytes), same: a.bytes === b.bytes },
    { label: "Loudness (integrated)", a: a.lufs === null ? "n/a" : `${a.lufs.toFixed(1)} LUFS`, b: b.lufs === null ? "n/a" : `${b.lufs.toFixed(1)} LUFS`, same: a.lufs === null || b.lufs === null ? null : Math.abs(a.lufs - b.lufs) < 0.5 },
    { label: "Peak (sample)", a: fmtDbVal(a.peakDb, "dBFS"), b: fmtDbVal(b.peakDb, "dBFS"), same: Number.isFinite(a.peakDb) && Number.isFinite(b.peakDb) ? Math.abs(a.peakDb - b.peakDb) < 0.5 : a.peakDb === b.peakDb },
  ];
}

/** Plain-text result that can be copied; contains file names and numbers only, never audio. */
export function summaryText(rows: PropertyRow[], a: FileFacts, b: FileFacts, headline: string, scorePct: number, offsetLine: string, differs: string): string {
  const lines = [
    `Audio comparison: ${a.name} vs ${b.name}`,
    `Match: ${scorePct.toFixed(1)}% - ${headline}`,
  ];
  if (offsetLine) lines.push(offsetLine);
  if (differs) lines.push(differs);
  lines.push("", "Property | File 1 | File 2 | Same?");
  for (const r of rows) lines.push(`${r.label} | ${r.a} | ${r.b} | ${r.same === null ? "unknown" : r.same ? "same" : "different"}`);
  lines.push("", "Compared in the browser at freedailypro.com/tool/audio-comparer; no audio was uploaded.");
  return lines.join("\n");
}
